import IterableDataProcessor from "../entities/IterableDataProcessor.js";
import AggregatorType from "../enums/AggregatorType.js";
import NumberFormatter from "../formatters/NumberFormatter.js";
import EnumValueProvider from "../providers/EnumValueProvider.js";

class RankingStatsAggregator{
    constructor(customResultsProcessor, numberFormatter) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }

        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.numberFormatter = numberFormatter || new NumberFormatter(3);
    }

    * aggregate(rankingScopesAndParameters, parameters, groupBy, defaultGroupByValues) {
        if (!(rankingScopesAndParameters instanceof Array)) {
            throw new TypeError('rankingScopesAndParameters must be an array');
        }
        if (typeof(parameters) !== 'undefined' && !(parameters instanceof Array)) {
            throw new TypeError('parameters must be undefined or an array');
        }
        if (typeof(defaultGroupByValues) !== 'undefined' && !(defaultGroupByValues instanceof Array)) {
            throw new TypeError('defaultGroupByValues must be undefined or an array');
        }
        if (!(groupBy instanceof Array)) {
            throw new TypeError('groupBy must be an array');
        }

        let paramsMapped = null;
        if (parameters) {
            paramsMapped = parameters.map(p => typeof(p) === 'object' ? p.key: p);
        }

        // build lookup table
        const rspIndexesPerUniqueGroupId = this.buildLookupTable(rankingScopesAndParameters, paramsMapped, groupBy, defaultGroupByValues);
        for (const statsPerGroup of rspIndexesPerUniqueGroupId) {
            const { groupId, scopes } = statsPerGroup;

            const parametersGroup = {};
            const aggregatedScopes = [];
            for (let rsp = 0; rsp < scopes.length; rsp++) {
                const scopeParams = rankingScopesAndParameters[scopes[rsp]];
                aggregatedScopes.push(scopeParams.scope);

                for (let p = 0; p < scopeParams.parameters.length; p++) {
                    const parameter = scopeParams.parameters[p];

                    // TODO - optimize, it happens twice
                    if (paramsMapped && !paramsMapped.includes(parameter.parameter)) {
                        continue;
                    }

                    if (!parametersGroup[parameter.parameter]) {
                        parametersGroup[parameter.parameter] = [parameter]
                    }
                    else {
                        parametersGroup[parameter.parameter].push(parameter);
                    }
                }
            }

            // now, lets aggregate
            const paramsAggregated = [];
            for (const paramName in parametersGroup) {
                if (!Object.hasOwnProperty.call(parametersGroup, paramName)) {
                    continue;
                }
      
                 const aggregated = this.aggregateParameters(parametersGroup[paramName]);
                 paramsAggregated.push(aggregated);
            }

            const scope = {};
            for (let g = 0; g < groupBy.length; g++) {
                const groupProp = groupBy[g];
                scope[groupProp] = groupId[g];
            }

            yield {
                scope,
                scopes: aggregatedScopes,
                // groupId,
                parameters: paramsAggregated
            };
        }
    }

    aggregateParameters(parametersGroup) {
        if (parametersGroup.length === 0) {
            throw new TypeError(`cannot aggregate without source parameters`);
        }

        const template = Object.assign({}, parametersGroup[0]);
        if (!template.aggregator) {
            throw new TypeError(`aggregator is not set for ${template.parameter}`);
        }

        const weight = parametersGroup.reduce((carry, parameter) => {
            return parameter.weight + carry;
        }, 0);

        if (weight === 0) {
            throw new Error(`cannot aggregate ${template.parameter} - weight is equal to 0`);
        }

        let aggregated = NaN;

        switch (template.aggregator) {
            case AggregatorType.AVG.key:
                aggregated = parametersGroup.reduce((carry, parameter) => {
                    return parameter.weight * parameter.value + carry;
                }, 0) / weight;
                break;

            case AggregatorType.MAX.key:
                aggregated = parametersGroup.reduce((carry, parameter) => {
                    return Math.max(carry, parameter.value);
                }, Number.MIN_VALUE);
                break;

            case AggregatorType.MIN.key:
                aggregated = parametersGroup.reduce((carry, parameter) => {
                    return Math.min(carry, parameter.value);
                }, Number.MAX_VALUE);
                break;

            case AggregatorType.SUM.key:
                aggregated = parametersGroup.reduce((carry, parameter) => {
                    return carry + parameter.value;
                }, 0);
                break;

            // TODO - it is actually the same as SUM
            case AggregatorType.COUNT.key:
                aggregated = parametersGroup.reduce((carry, parameter) => {
                    return carry + parameter.value;
                }, 0);
                break;
        
            default:
                throw new Error(`Unknown type of aggregator: ${template.aggregator} for ${template.parameter}`);
        }

        template.weight = weight;
        template.value = this.numberFormatter.format(aggregated);

        return template;
    }

    buildLookupTable(rankingScopesAndParameters, parameters, groupBy, defaultGroupByValues) {
        const rspIndexesPerUniqueGroupId = [];
        for (let rsp = 0; rsp < rankingScopesAndParameters.length; rsp++) {
            const scopeParams = rankingScopesAndParameters[rsp];

            if (parameters && !scopeParams.parameters.some(p => parameters.includes(p.parameter))) {
                continue;
            }

            const groupId = [];
            for (let g = 0; g < groupBy.length; g++) {
                const groupProperty = groupBy[g];
                let groupValue = this.resultsProcessor.getObjectContent(scopeParams.scope, [groupProperty]);
                if (defaultGroupByValues && typeof(groupValue) === 'undefined') {
                    groupValue = defaultGroupByValues[g];
                }
                groupId.push(groupValue);
            }

            // check if such group already exists
            const existingGroup = rspIndexesPerUniqueGroupId.findIndex(ex => ex.groupId.every((v, i) => groupId[i] === v));
            if (existingGroup === -1) {
                rspIndexesPerUniqueGroupId.push({
                    groupId, scopes: [rsp]
                })
            }
            else {
                rspIndexesPerUniqueGroupId[existingGroup].scopes.push(rsp);
            }
        }

        return rspIndexesPerUniqueGroupId;
    }
}

export default RankingStatsAggregator;